import ResponsiveSidebar from "../../General/ResponsiveSidebar/ResponsiveSidebar";
import React from "react";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Tooltip,
  Collapse,
  Box,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  CarCrash as CarCrashIcon,
  WarningAmber as WarningAmberIcon,
  MenuBook as MenuBookIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Videocam as VideocamIcon,
  FactCheck as FactCheckIcon,
  ExpandLess,
  ExpandMore,
  FiberManualRecord as FiberManualRecordIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const drawerWidth = 240;
const collapsedWidth = 60;

interface SidebarProps {
  open?: boolean;
  toggle?: () => void;
}

type SafetyChildMenuItem = {
  text: string;
  path: string;
  icon?: React.ReactNode;
};

type SafetyMenuItem = {
  text: string;
  icon: React.ReactNode;
  path?: string;
  children?: SafetyChildMenuItem[];
};

const SafetySidebar = ({ open = false, toggle = () => {} }: SidebarProps) => {
  const location = useLocation();

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const safetyMenuItems: SafetyMenuItem[] = useMemo(
    () => [
      { text: "Dashboard", icon: <DashboardIcon />, path: "/safety" },
      { text: "Accidents", icon: <CarCrashIcon />, path: "/safety/accidents" },
      {
        text: "Harsh Events",
        icon: <WarningAmberIcon />,
        path: "/safety/harshEvents",
      },
      {
        text: "Logbook Check",
        icon: <MenuBookIcon />,
        path: "/safety/logbookChecks",
      },
      {
        text: "Camera Check",
        icon: <VideocamIcon />,
        path: "/safety/cameraChecks",
      },
      {
        text: "Pre-Trip Inspection",
        icon: <FactCheckIcon />,
        children: [
          {
            text: "Calendar",
            path: "/safety/preTripInspectionChecks",
          },
          {
            text: "Missed Pre Trips",
            path: "/safety/preTripInspectionChecks/missed",
          },
        ],
      },
    ],
    [],
  );

  useEffect(() => {
    const nextOpenSections: Record<string, boolean> = {};

    safetyMenuItems.forEach((item) => {
      if (item.children) {
        nextOpenSections[item.text] = item.children.some((child) =>
          location.pathname.startsWith(child.path),
        );
      }
    });

    setOpenSections((prev) => ({ ...prev, ...nextOpenSections }));
  }, [location.pathname, safetyMenuItems]);

  const isSelected = (path: string) => location.pathname.startsWith(path);

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const renderNavButton = (item: SafetyMenuItem | SafetyChildMenuItem) => {
    const isParent = (item as SafetyMenuItem).children !== undefined;
    const itemPath =
      (item as SafetyMenuItem).path || (item as SafetyChildMenuItem).path;
    const isChildSelected = isParent
      ? (item as SafetyMenuItem).children?.some((child) =>
          isSelected(child.path),
        )
      : itemPath
        ? isSelected(itemPath)
        : false;

    const button = (
      <ListItemButton
        key={item.text}
        selected={isChildSelected}
        {...(itemPath ? { component: RouterLink, to: itemPath } : {})}
        sx={{
          justifyContent: open ? "initial" : "center",
          pl: isParent ? 2 : 4,
        }}
        onClick={(event) => {
          if (isParent) {
            event.preventDefault();
            toggleSection(item.text);
          }
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 3 : 0,
            justifyContent: "center",
          }}
        >
          {(item as SafetyMenuItem).icon || (
            <FiberManualRecordIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
        {isParent &&
          (openSections[item.text] ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>
    );

    return open ? (
      button
    ) : (
      <Tooltip key={item.text} title={item.text} placement="right">
        {button}
      </Tooltip>
    );
  };

  return (
    <ResponsiveSidebar
      open={open}
      onClose={toggle}
      sx={(theme) => ({
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? drawerWidth : collapsedWidth,
          boxSizing: "border-box",
          top: 64,
          height: "calc(100% - 64px)",
          overflowX: "hidden",
          position: "fixed",
          transition: theme.transitions.create("width", {
            duration: theme.transitions.duration.standard,
          }),
        },
      })}
    >
      <Toolbar sx={{ justifyContent: open ? "flex-end" : "center" }}>
        <IconButton
          onClick={toggle}
          aria-label={
            open ? "Close section navigation" : "Open section navigation"
          }
        >
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </Toolbar>
      <List>
        {safetyMenuItems.map((item) => (
          <Box key={item.text}>
            {renderNavButton(item)}
            {item.children && (
              <Collapse
                in={openSections[item.text]}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {item.children.map((child) =>
                    renderNavButton({
                      ...child,
                      icon: <FiberManualRecordIcon fontSize="small" />,
                    }),
                  )}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>
    </ResponsiveSidebar>
  );
};

export default SafetySidebar;
